import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Text,
  Stack,
  IconButton,
  Button,
  Link,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  AdvancedSearchURLParams,
  ConsolidatedFilter,
  ConsolidatedFilterInfo,
  ConsolidatedSidebar,
  parseConsolidatedFilterInfo,
} from '@ltvco/refresh-lib/v2';
import { ArrowBack, Close, Tune, TuneRounded } from '@mui/icons-material';
import { useState, useRef, Dispatch, SetStateAction, useContext } from 'react';

import { SidebarContainer, SubmitButton } from './components';

export interface ResultFilterProps {
  advancedSearchUrlParams: AdvancedSearchURLParams;
  onCollapse?: () => void;
  parentSidebarCollapsed?: boolean;
  setParentSidebarCollapsed?: Dispatch<SetStateAction<boolean>>;
  userInExactAgeInputVariation?: boolean;
  /** Start with Reports accordion collapsed (e.g., for Contact Pro) */
  reportsAccordionCollapsed?: boolean;
}

export function PersonFilters({
  advancedSearchUrlParams,
  onCollapse,
  parentSidebarCollapsed,
  setParentSidebarCollapsed,
  userInExactAgeInputVariation,
  reportsAccordionCollapsed,
}: ResultFilterProps) {
  const { trackEventGA4 } = useContext(AppConfig);
  const formRef = useRef<HTMLFormElement>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isMedium, isMobile } = useScreenSize();
  const consolidatedFilterDefaults = parseConsolidatedFilterInfo(
    advancedSearchUrlParams
  );
  const filterRef = useRef<{
    clearFields: () => void;
    submitForm: () => void;
  } | null>(null);

  const handleExternalSubmit = () => {
    if (filterRef.current) {
      filterRef.current.submitForm();
    }
  };

  const handleExternalClear = () => {
    if (filterRef.current) {
      filterRef.current.clearFields();
    }
  };

  const collapsible = {
    isCollapsed:
      parentSidebarCollapsed !== undefined
        ? parentSidebarCollapsed
        : isSidebarCollapsed,
    setIsCollapsed:
      setParentSidebarCollapsed !== undefined
        ? setParentSidebarCollapsed
        : setIsSidebarCollapsed,
  };

  const ShowExternalButton = () => {
    if (isDesktop) {
      return (
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: '20px 0',
            borderRadius: '10px',
          }}
        >
          <Divider sx={{ mb: 2 }} />
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Link
              onClick={() => handleExternalClear()}
              data-cy="clear_all_fields_button"
              sx={{
                cursor: 'pointer',
                margin: '0 10px',
                width: '50%',
              }}
            >
              <Text
                variant="h5"
                sx={(theme) => ({
                  color: theme.palette.primary.main,
                  marginBottom: '0',
                  textAlign: 'center',
                })}
              >
                Clear all
              </Text>
            </Link>

            <SubmitButton
              id="filters-toggle"
              type="button"
              name="filters-toggle"
              variant="contained"
              fullWidth
              onClick={handleExternalSubmit}
              sx={{
                display: isDesktop ? 'block' : 'none',
                width: '72%',
              }}
            >
              Show Results
            </SubmitButton>
          </Stack>
        </Box>
      );
    }
  };

  const filterHeaderStyles = () => {
    return {
      top: isMobile ? '72px' : '0px',
      zIndex: 200,
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: 0,
      padding: isMobile ? '10px 20px 0px 20px' : '20px 20px 0px 20px',
      paddingTop: isMobile ? '40px' : '20px',
      backgroundColor: 'white',
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexDirection: 'row',
      position: 'sticky',
    };
  };
  return (
    <Box
      sx={{
        border: isTabletOrMobile ? 'unset' : '1px solid',
        borderColor: isTabletOrMobile ? 'unset' : 'divider',
        borderRadius: isTabletOrMobile ? 'unset' : '10px',
        marginLeft: 'unset',
        position: 'relative',
        backgroundColor: !isSidebarCollapsed
          ? theme.palette.background.paper
          : 'none',
        height: isTabletOrMobile ? 'unset' : '92vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* HEADER - Sticky */}
      <Box
        sx={{
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1200,
          backgroundColor: theme.palette.background.paper,
          borderBottom: isTabletOrMobile
            ? `1px solid ${theme.palette.divider}`
            : 'none',
          borderRadius: isTabletOrMobile ? 'unset' : '10px 10px 0 0',
        }}
      >
        {isDesktop && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: '100%',
              paddingTop: '28px',
              padding: '28px 20px 10px 20px',
              borderRadius: '10px',
            }}
          >
            <Stack
              flexDirection="row"
              gap={1}
              alignItems={'center'}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <TuneRounded fontSize="medium" sx={{ fontWeight: 'bold' }} />
              <Text variant="h3" m={0}>
                Filter results
              </Text>
            </Stack>

            <IconButton
              onClick={() => {
                trackEventGA4({
                  event_name: 'search_results_interaction',
                  type: 'close_filter',
                });
                onCollapse?.();
              }}
              size="small"
            >
              <ArrowBack />
            </IconButton>
          </Stack>
        )}
        {isMedium && !collapsible.isCollapsed && (
          <Stack sx={filterHeaderStyles()}>
            <Text variant="h3">
              <Tune sx={{ m: 0.75, mb: -0.75 }} color={'inherit'} />
              Filter results
            </Text>
            <Button
              id="filters-toggle"
              type="button"
              name="filters-toggle"
              onClick={() => {
                trackEventGA4({
                  event_name: 'search_results_interaction',
                  type: 'close_filter',
                });
                collapsible.setIsCollapsed(!collapsible.isCollapsed);
              }}
              sx={(theme) => ({
                alignSelf: 'flex-start',
                color: `${theme.palette.text.secondary}`,
                '&:hover': {
                  background: 'transparent',
                },

                [theme.breakpoints.up('md')]: {
                  display: 'none',
                },
              })}
            >
              <Close />
            </Button>
          </Stack>
        )}
      </Box>

      {/* BODY - Scrollable */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SidebarContainer
          sx={{
            borderRadius: '10px',
            paddingTop: isMobile ? '26px' : 'unset',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ConsolidatedSidebar
            hideSubmitButton={true}
            collapsible={collapsible}
            handleExternalSubmit={handleExternalSubmit}
          >
            <ConsolidatedFilter
              ref={filterRef}
              formRef={formRef}
              defaults={consolidatedFilterDefaults as ConsolidatedFilterInfo}
              isSidebarCollapsed={
                parentSidebarCollapsed !== undefined
                  ? parentSidebarCollapsed
                  : isSidebarCollapsed
              }
              setIsSidebarCollapsed={
                setParentSidebarCollapsed !== undefined
                  ? setParentSidebarCollapsed
                  : setIsSidebarCollapsed
              }
              userInExactAgeInputVariation={userInExactAgeInputVariation}
              reportsAccordionCollapsed={reportsAccordionCollapsed}
            />
          </ConsolidatedSidebar>
        </SidebarContainer>
      </Box>

      {/* FOOTER - Sticky */}
      <Box
        sx={{
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          zIndex: 1200,
          backgroundColor: theme.palette.background.paper,
          borderTop: 'none',
          borderRadius: isTabletOrMobile ? 'unset' : '0 0 10px 10px',
        }}
      >
        <ShowExternalButton />
      </Box>
    </Box>
  );
}
